import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';

const ORANGE = '#E65100';

// Hardcoded events per status tier (newest event first)
const SCHEDULED_EVENTS = [
  { date: '01-MAY-2026', time: '08:20:20', description: 'Shipment has been scheduled.',      latest: true  },
];

const DISPATCHED_EVENTS = [
  { date: '03-MAY-2026', time: '16:40:00', description: 'The shipment has been dispatched.', latest: true  },
  { date: '01-MAY-2026', time: '08:20:20', description: 'Shipment has been scheduled.',      latest: false },
];

const POST_DELIVERY_EVENTS = [
  { date: '01-JUL-2026', time: '10:11:07', description: 'Goods delivered at Consignee',      latest: true  },
  { date: '29-JUN-2026', time: '08:20:20', description: 'The shipment has been dispatched.', latest: false },
  { date: '28-JUN-2026', time: '12:12:04', description: 'Shipment has been scheduled.',      latest: false },
];

const POST_DELIVERY_STATUSES = ['Goods Received', 'Consignee GRN Generated'];
const DISPATCHED_STATUSES    = ['Shipped', 'Proforma Requested', 'Proforma Uploaded', 'Proforma Digitalized', 'Proforma Sent'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const useStyles = makeStyles(() => ({
  paper: {
    width: 820,
    maxWidth: '96vw',
    borderRadius: 8,
    // The timeline rows below use plain <div>s (not MUI Typography). Inside the
    // Dialog's portal they'd inherit the browser default serif (Times New Roman),
    // so pin the app font here and every descendant inherits it.
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 24px 8px',
    position: 'relative',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.5rem',
  },
  cancelBtn: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: '4px 8px',
    fontSize: '0.9rem',
    color: '#555',
    fontFamily: 'inherit',
    '&:hover': { opacity: 0.75 },
  },
  closeIcon: {
    color: '#e53935',
    fontSize: 22,
  },
  content: {
    padding: '12px 24px 28px',
  },
  // Info ribbon
  infoTableWrap: {
    backgroundColor: '#EEF2F7',
    borderRadius: 9,
    marginBottom: 24,
    overflow: 'hidden',
    boxShadow: 'none',
    border: 'none',
  },
  infoHeadCell: {
    fontWeight: 700,
    fontSize: '0.875rem',
    color: '#111',
    paddingTop: 24,
    paddingBottom: 6,
    paddingLeft: 32,
    paddingRight: 56,
    borderBottom: 'none',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  infoCell: {
    fontWeight: 400,
    fontSize: '0.875rem',
    color: '#111',
    paddingTop: 0,
    paddingBottom: 24,
    paddingLeft: 32,
    paddingRight: 56,
    borderBottom: 'none',
    backgroundColor: 'transparent',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  // Section header
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    marginBottom: 4,
    color: '#222',
  },
  sectionBar: {
    height: 3,
    width: 48,
    backgroundColor: ORANGE,
    borderRadius: 2,
    marginBottom: 16,
  },
  // Timeline
  timelineWrapper: {
    paddingLeft: 8,
    marginBottom: 28,
  },
  timelineRow: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  timelineTrack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 22,
    flexShrink: 0,
  },
  topLine: {
    width: 2,
    height: 14,
    borderLeft: `2px dashed ${ORANGE}`,
    opacity: 0.55,
  },
  dotEmpty: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `2px solid ${ORANGE}`,
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  dotFilled: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: ORANGE,
    flexShrink: 0,
  },
  connectorLine: {
    flex: 1,
    minHeight: 30,
    borderLeft: `2px dashed ${ORANGE}`,
    opacity: 0.55,
  },
  timelineContent: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingBottom: 4,
    marginTop: -1,
    paddingLeft: 12,
  },
  timelineDateCol: {
    minWidth: 120,
    marginRight: 20,
  },
  timelineDate: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#111',
    lineHeight: 1.4,
  },
  timelineTime: {
    fontSize: '0.875rem',
    color: '#111',
    lineHeight: 1.4,
  },
  timelineDesc: {
    fontSize: '0.875rem',
    color: '#111',
    paddingTop: 1,
  },
  // Container details table
  containerTableWrap: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    marginTop: 8,
  },
  containerHeadCell: {
    fontWeight: 700,
    backgroundColor: '#f5f5f5',
    padding: '8px 10px',
    fontSize: '0.78rem',
    whiteSpace: 'pre-line',
    lineHeight: 1.3,
    color: '#333',
    verticalAlign: 'bottom',
  },
  containerCell: {
    padding: '8px 10px',
    fontSize: '0.8rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
  },
  noData: {
    textAlign: 'center',
    padding: 24,
    color: '#9e9e9e',
    fontSize: '0.875rem',
  },
}));

const CONTAINER_COLUMNS = [
  { key: 'vendorBatchNumber',  label: 'FG Batch\nNumber',                      align: 'left'  },
  { key: 'productDesc',        label: 'Sponsor Product Code /\nDescription',   align: 'left'  },
  { key: 'producedQuantity',   label: 'Quantity',                              align: 'right' },
  { key: 'noOfPallets',        label: 'Number of\nPallets',                    align: 'right' },
  { key: 'netWeight',          label: 'Net\nWeight',                           align: 'right' },
  { key: 'grossWeight',        label: 'Gross\nWeight',                         align: 'right' },
  { key: 'batchUOM',           label: 'UoM',                                   align: 'left'  },
  { key: 'temperature',        label: 'Temperature',                           align: 'left'  },
];

const ShipmentMessagesDialog = ({ open, onClose, shipment, batchesList, batchesError }) => {
  const classes = useStyles();
  const events = POST_DELIVERY_STATUSES.includes(shipment?.statusDescription)
    ? POST_DELIVERY_EVENTS
    : DISPATCHED_STATUSES.includes(shipment?.statusDescription)
    ? DISPATCHED_EVENTS
    : SCHEDULED_EVENTS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      scroll="paper"
    >
      {/* Title row */}
      <div className={classes.titleRow}>
        <Typography className={classes.title}>Shipment Messages</Typography>
        <button className={classes.cancelBtn} onClick={onClose}>
          Cancel <CloseIcon className={classes.closeIcon} />
        </button>
      </div>

      <DialogContent className={classes.content}>
        {/* Info ribbon */}
        <TableContainer className={classes.infoTableWrap} component={Paper} elevation={0}>
          <Table size="small" style={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.infoHeadCell}>CMO</TableCell>
                <TableCell className={classes.infoHeadCell}>Sponsor Shipment ID</TableCell>
                <TableCell className={classes.infoHeadCell}>FF Schedule ID</TableCell>
                <TableCell className={classes.infoHeadCell} style={{ paddingRight: 32 }}>Dispatch Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.infoCell}>{shipment?.vendorName || ''}</TableCell>
                <TableCell className={classes.infoCell}>{shipment?.sponsorShipmentID || ''}</TableCell>
                <TableCell className={classes.infoCell}>{shipment?.freightForwarderScheduleID || ''}</TableCell>
                <TableCell className={classes.infoCell} style={{ paddingRight: 32 }}>
                  {shipment?.actualDispatchDate ? formatDate(shipment?.actualDispatchDate) : ""}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Shipment Events */}
        <Typography className={classes.sectionTitle}>Shipment Events</Typography>
        <div className={classes.sectionBar} />

        <div className={classes.timelineWrapper}>
          {events.map((ev, idx) => (
            <div key={idx} className={classes.timelineRow}>
              <div className={classes.timelineTrack}>
                <div className={classes.topLine} />
                <div className={ev.latest ? classes.dotEmpty : classes.dotFilled} />
                {idx < events.length - 1 && <div className={classes.connectorLine} />}
              </div>
              <div className={classes.timelineContent}>
                <div className={classes.timelineDateCol}>
                  <div className={classes.timelineDate}>{ev.date}</div>
                  <div className={classes.timelineTime}>{ev.time}</div>
                </div>
                <div className={classes.timelineDesc}>{ev.description}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Shipment Container Details */}
        <Typography className={classes.sectionTitle}>Shipment Container Details</Typography>
        <div className={classes.sectionBar} />

        <TableContainer className={classes.containerTableWrap} component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {CONTAINER_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    className={classes.containerHeadCell}
                    align={col.align}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {batchesError ? (
                <TableRow>
                  <TableCell colSpan={CONTAINER_COLUMNS.length} className={classes.noData} style={{ color: '#d32f2f' }}>
                    Something went wrong. Please try again.
                  </TableCell>
                </TableRow>
              ) : (!batchesList || batchesList.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={CONTAINER_COLUMNS.length} className={classes.noData}>
                    No data available.
                  </TableCell>
                </TableRow>
              ) : (
                batchesList.map((batch, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell className={classes.containerCell}>{batch.vendorBatchNumber}</TableCell>
                    <TableCell className={classes.containerCell}>
                      {batch.fgMaterialCode} / {batch.fgMaterialName}
                    </TableCell>
                    <TableCell className={classes.containerCell} align="right">{batch.producedQuantity}</TableCell>
                    <TableCell className={classes.containerCell} align="right">{batch.noOfPallets}</TableCell>
                    <TableCell className={classes.containerCell} align="right">{batch.netWeight}</TableCell>
                    <TableCell className={classes.containerCell} align="right">{batch.grossWeight}</TableCell>
                    <TableCell className={classes.containerCell}>{batch.batchUOM}</TableCell>
                    <TableCell className={classes.containerCell}>{batch.temperature}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ShipmentMessagesDialog;
